import { useEffect, type ReactNode } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  BellRing,
  CheckCircle2,
  ExternalLink,
  HelpCircle,
  LockKeyhole,
  Mail,
  ShieldCheck,
  Smartphone,
  Trash2,
  Users,
} from 'lucide-react';
import { Navigation, type Locale } from './components/Navigation';
import { SiteFooter } from './components/SiteFooter';
import { siteConfig } from './siteConfig';

type ResourceKey = 'support' | 'privacy' | 'delete';

type ResourceRoute = {
  key: ResourceKey;
  svPath: string;
  enPath: string;
};

const routes: ResourceRoute[] = [
  { key: 'support', svPath: '/fampo/support/', enPath: '/en/fampo/support/' },
  { key: 'privacy', svPath: '/fampo/privacy/', enPath: '/en/fampo/privacy/' },
  { key: 'delete', svPath: '/fampo/delete-account/', enPath: '/en/fampo/delete-account/' },
];

function resolveRoute() {
  const pathname = window.location.pathname;
  const locale: Locale = pathname.startsWith('/en/') ? 'en' : 'sv';
  const route =
    routes.find(
      (candidate) =>
        pathname === candidate.svPath ||
        pathname === candidate.svPath.slice(0, -1) ||
        pathname === candidate.enPath ||
        pathname === candidate.enPath.slice(0, -1),
    ) ?? routes[0];

  return { route, locale };
}

const resourceLinks = {
  sv: [
    ['/fampo/', 'Översikt'],
    ['/fampo/support/', 'Support'],
    ['/fampo/privacy/', 'Integritet'],
    ['/fampo/delete-account/', 'Radera konto'],
  ],
  en: [
    ['/en/fampo/', 'Overview'],
    ['/en/fampo/support/', 'Support'],
    ['/en/fampo/privacy/', 'Privacy'],
    ['/en/fampo/delete-account/', 'Delete account'],
  ],
} as const;

export default function FampoResourcePage() {
  const { route, locale } = resolveRoute();

  useEffect(() => {
    document.documentElement.lang = locale === 'sv' ? 'sv-SE' : 'en';
  }, [locale]);

  const changeLocale = (nextLocale: Locale) => {
    if (nextLocale === locale) return;
    window.location.assign(nextLocale === 'en' ? route.enPath : route.svPath);
  };

  return (
    <div className="min-h-screen bg-[#F7F3FF] text-[#21143D]">
      <Navigation locale={locale} onLocaleChange={changeLocale} />

      <nav
        className="sticky top-24 z-40 border-y border-[#DED5F3]/80 bg-[#F7F3FF]/95 backdrop-blur-xl"
        aria-label={locale === 'sv' ? 'FamPo-resurser' : 'FamPo resources'}
      >
        <div className="mx-auto flex max-w-[1440px] items-center gap-1 overflow-x-auto px-5 py-2 md:px-10 lg:px-16">
          <span className="mr-3 shrink-0 font-sora text-sm font-extrabold text-[#2D1457]">FamPo</span>
          {resourceLinks[locale].map(([href, label]) => (
            <a
              key={href}
              href={href}
              className={
                'shrink-0 rounded-full px-3 py-2 text-xs font-bold transition ' +
                (window.location.pathname === href || window.location.pathname === href.slice(0, -1)
                  ? 'bg-white text-[#7C3AED] shadow-sm'
                  : 'text-[#6B5A80] hover:bg-white hover:text-[#7C3AED]')
              }
            >
              {label}
            </a>
          ))}
        </div>
      </nav>

      {route.key === 'support' && <SupportPage locale={locale} />}
      {route.key === 'privacy' && <PrivacyPage locale={locale} />}
      {route.key === 'delete' && <DeleteAccountPage locale={locale} />}

      <SiteFooter locale={locale} productContext="fampo" />
    </div>
  );
}

function ResourceHero({
  locale,
  eyebrow,
  title,
  intro,
  icon: Icon,
}: {
  locale: Locale;
  eyebrow: string;
  title: string;
  intro: string;
  icon: typeof HelpCircle;
}) {
  return (
    <section className="relative overflow-hidden px-5 pb-16 pt-20 md:px-10 md:pb-20 md:pt-24 lg:px-16">
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div className="absolute right-[8%] top-4 h-72 w-72 rounded-full bg-[#EC4899]/10 blur-3xl" />
        <div className="absolute left-[8%] top-24 h-72 w-72 rounded-full bg-[#7C3AED]/12 blur-3xl" />
      </div>
      <div className="relative mx-auto max-w-[1180px]">
        <a
          href={locale === 'sv' ? '/fampo/' : '/en/fampo/'}
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#6B5A80] transition hover:text-[#7C3AED]"
        >
          <ArrowLeft size={16} />
          {locale === 'sv' ? 'Till FamPo' : 'Back to FamPo'}
        </a>

        <div className="mt-10 flex h-14 w-14 items-center justify-center rounded-[20px] bg-[#7C3AED] text-white shadow-[0_16px_36px_rgba(124,58,237,0.18)]">
          <Icon size={25} />
        </div>

        <p className="mt-7 text-xs font-bold uppercase tracking-[0.14em] text-[#7C3AED]">{eyebrow}</p>
        <h1 className="mt-4 max-w-[900px] font-sora text-5xl font-extrabold leading-[1.04] tracking-[-0.05em] text-[#2D1457] md:text-7xl">
          {title}
        </h1>
        <p className="mt-6 max-w-[760px] text-lg leading-8 text-[#68577D] md:text-xl">{intro}</p>
      </div>
    </section>
  );
}

function SupportPage({ locale }: { locale: Locale }) {
  const sv = locale === 'sv';

  const subject = 'FamPo support';
  const supportAreas: Array<[typeof Users, string, string]> = [
    [Users, sv ? 'Konto & familj' : 'Account & family', sv ? 'Inloggning, familj, profiler och föräldraläge.' : 'Sign-in, family, profiles and parent mode.'],
    [Smartphone, sv ? 'Parkoppling' : 'Pairing', sv ? 'Koppla en barnenhet till en befintlig barnprofil.' : 'Pair a child device to an existing child profile.'],
    [BellRing, sv ? 'Notiser' : 'Notifications', sv ? 'Pushnotiser och Android-behörigheter.' : 'Push notifications and Android permissions.'],
    [ShieldCheck, sv ? 'Integritet & radering' : 'Privacy & deletion', sv ? 'Databehandling, integritet och radering av familjekonto.' : 'Data handling, privacy and family-account deletion.'],
  ];

  return (
    <main>
      <ResourceHero
        locale={locale}
        eyebrow={sv ? 'FamPo Support' : 'FamPo Support'}
        title={sv ? 'Hjälp med FamPo.' : 'Help with FamPo.'}
        intro={
          sv
            ? 'Här hittar du den officiella supportvägen för FamPo. Du kan få hjälp med konto, parkoppling, uppgifter, belöningar, notiser, integritet och kontoradering.'
            : 'This is the official support route for FamPo. You can get help with accounts, pairing, tasks, rewards, notifications, privacy and account deletion.'
        }
        icon={HelpCircle}
      />

      <section className="px-5 pb-24 md:px-10 lg:px-16">
        <div className="mx-auto grid max-w-[1180px] gap-6 lg:grid-cols-[1.05fr_.95fr]">
          <article className="rounded-[30px] border border-[#E3DBEF] bg-white p-7 shadow-[0_18px_50px_rgba(70,45,120,0.06)] md:p-9">
            <div className="flex items-center gap-4">
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#F0E8FF] text-[#7C3AED]">
                <Mail size={22} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#8A789F]">
                  {sv ? 'Direkt support' : 'Direct support'}
                </p>
                <h2 className="mt-1 font-sora text-2xl font-bold text-[#2D1457]">{siteConfig.email}</h2>
              </div>
            </div>

            <p className="mt-6 leading-7 text-[#68577D]">
              {sv
                ? 'Beskriv vad som händer, vilken enhet du använder och vad du förväntade dig att FamPo skulle göra. Skicka inte lösenord, PIN-koder eller andra hemligheter.'
                : 'Describe what happened, which device you are using and what you expected FamPo to do. Do not send passwords, PIN codes or other secrets.'}
            </p>

            <a
              href={`mailto:${siteConfig.email}?subject=${encodeURIComponent(subject)}`}
              className="mt-7 inline-flex min-h-12 items-center gap-2 rounded-full bg-[#7C3AED] px-6 font-bold text-white transition hover:bg-[#6D28D9]"
            >
              <Mail size={18} />
              {sv ? 'Mejla FamPo-supporten' : 'Email FamPo support'}
            </a>
          </article>

          <article className="rounded-[30px] border border-[#E3DBEF] bg-[#FCFAFF] p-7 md:p-9">
            <div className="flex items-center gap-4">
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#F0E8FF] text-[#7C3AED]">
                <Smartphone size={22} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#8A789F]">
                  {sv ? 'I appen' : 'In the app'}
                </p>
                <h2 className="mt-1 font-sora text-2xl font-bold text-[#2D1457]">
                  {sv ? 'Hjälp & support' : 'Help & support'}
                </h2>
              </div>
            </div>
            <p className="mt-6 leading-7 text-[#68577D]">
              {sv
                ? 'FamPo innehåller ett inbyggt hjälp- och supportflöde där du kan skicka bugg, förslag eller fråga. Om formuläret inte kan skickas finns e-post som reservväg.'
                : 'FamPo includes an in-app help and support flow where you can send a bug report, idea or question. Email is available as a fallback if the form cannot be sent.'}
            </p>
          </article>
        </div>

        <div className="mx-auto mt-8 grid max-w-[1180px] gap-5 md:grid-cols-2 lg:grid-cols-4">
          {supportAreas.map(([Icon, title, body]) => (
            <article key={String(title)} className="rounded-[26px] border border-[#E6DFF0] bg-white p-6">
              <Icon size={21} className="text-[#7C3AED]" />
              <h2 className="mt-4 font-sora text-lg font-bold text-[#2D1457]">{String(title)}</h2>
              <p className="mt-2 text-sm leading-6 text-[#6B5A80]">{String(body)}</p>
            </article>
          ))}
        </div>

        <div className="mx-auto mt-8 grid max-w-[1180px] gap-5 md:grid-cols-2">
          <a
            href={sv ? '/fampo/privacy/' : '/en/fampo/privacy/'}
            className="group rounded-[28px] border border-[#DED5EA] bg-white p-7 transition hover:border-[#A78BFA]"
          >
            <LockKeyhole size={22} className="text-[#7C3AED]" />
            <h2 className="mt-4 font-sora text-xl font-bold text-[#2D1457]">
              {sv ? 'Integritetspolicy' : 'Privacy policy'}
            </h2>
            <p className="mt-2 leading-7 text-[#6B5A80]">
              {sv ? 'Läs vilka uppgifter FamPo behandlar och vilka tjänster som används.' : 'Read what data FamPo processes and which services are used.'}
            </p>
            <span className="mt-5 inline-flex items-center gap-2 font-bold text-[#7C3AED]">
              {sv ? 'Öppna policy' : 'Open policy'} <ArrowRight size={16} />
            </span>
          </a>

          <a
            href={sv ? '/fampo/delete-account/' : '/en/fampo/delete-account/'}
            className="group rounded-[28px] border border-[#DED5EA] bg-white p-7 transition hover:border-[#A78BFA]"
          >
            <Trash2 size={22} className="text-[#7C3AED]" />
            <h2 className="mt-4 font-sora text-xl font-bold text-[#2D1457]">
              {sv ? 'Radera familjekonto' : 'Delete family account'}
            </h2>
            <p className="mt-2 leading-7 text-[#6B5A80]">
              {sv ? 'Se appvägen, den externa begäransvägen och vilken data som omfattas.' : 'See the in-app path, external request route and what data is covered.'}
            </p>
            <span className="mt-5 inline-flex items-center gap-2 font-bold text-[#7C3AED]">
              {sv ? 'Se raderingsvägen' : 'View deletion route'} <ArrowRight size={16} />
            </span>
          </a>
        </div>
      </section>
    </main>
  );
}

function PrivacyPage({ locale }: { locale: Locale }) {
  const sv = locale === 'sv';

  return (
    <main>
      <ResourceHero
        locale={locale}
        eyebrow={sv ? 'Integritetspolicy' : 'Privacy Policy'}
        title={sv ? 'Så behandlar FamPo data.' : 'How FamPo handles data.'}
        intro={
          sv
            ? 'Den här policyn beskriver FamPo 1.0:s nuvarande källkontrakt och releaseförberedda dataflöden. Butiksdeklarationer och den driftsatta versionen ska hållas i linje med samma faktiska beteende.'
            : 'This policy describes the current FamPo 1.0 source contract and release-preparation data flows. Store disclosures and the deployed version are intended to stay aligned with the same actual behavior.'
        }
        icon={ShieldCheck}
      />

      <section className="px-5 pb-24 md:px-10 lg:px-16">
        <div className="mx-auto max-w-[920px] space-y-5">
          <PolicyCard title={sv ? 'Ansvarig tjänst och kontakt' : 'Service and contact'}>
            <p>
              {sv
                ? 'FamPo utvecklas och tillhandahålls av Keevo, enskild firma med bas i Sverige. Frågor om integritet, databehandling eller radering kan skickas till '
                : 'FamPo is developed and provided by Keevo, a sole proprietorship based in Sweden. Questions about privacy, data handling or deletion can be sent to '}
              <a href={`mailto:${siteConfig.email}`} className="font-bold text-[#7C3AED] underline underline-offset-2">
                {siteConfig.email}
              </a>.
            </p>
          </PolicyCard>

          <PolicyCard title={sv ? 'Vilka uppgifter omfattas?' : 'What data is covered?'}>
            <PolicyList
              items={
                sv
                  ? [
                      ['Vuxet konto', 'e-postadress och inloggningsuppgifter för den vuxna som använder och hanterar familjekontot.'],
                      ['Familje- och appdata', 'förnamn eller smeknamn, profiler, uppgifter, poäng, belöningar, önskningar, notiser och annat innehåll som familjen skapar i appen.'],
                      ['Parad barnenhet', 'en barnenhet kopplas till en befintlig barnprofil genom en engångskod och använder en begränsad anonym teknisk session. Det är inte ett vanligt barnkonto med e-post eller lösenord.'],
                      ['Enhets- och pushuppgifter', 'lokalt enhetsregistrerings-ID och, när notiser tillåts, registreringsuppgifter/token för pushnotiser.'],
                      ['Supportärenden', 'meddelande, kategori och eventuell svarse-post som användaren väljer att skicka via supportflödet.'],
                    ]
                  : [
                      ['Adult account', 'email address and sign-in credentials for the adult who uses and manages the family account.'],
                      ['Family and app data', 'first names or nicknames, profiles, tasks, points, rewards, wishes, notifications and other content created by the family in the app.'],
                      ['Paired child device', 'a child device is linked to an existing child profile using a one-time code and uses a restricted anonymous technical session. It is not a normal child account with email or password.'],
                      ['Device and push data', 'a local device-registration identifier and, when notifications are allowed, push-notification registration data/token.'],
                      ['Support requests', 'the message, category and any reply email that the user chooses to send through the support flow.'],
                    ]
              }
            />
          </PolicyCard>

          <PolicyCard title={sv ? 'Varför används uppgifterna?' : 'Why is the data used?'}>
            <p>
              {sv
                ? 'Uppgifterna används för att tillhandahålla vuxeninloggning, familjesynk, barnprofiler, uppgifter, godkännanden, poäng, belöningar, parkoppling, notiser, support och säker konto-/enhetshantering. FamPo ska inte samla in mer information än vad funktionerna kräver.'
                : 'The data is used to provide adult sign-in, family sync, child profiles, tasks, approvals, points, rewards, pairing, notifications, support and secure account/device handling. FamPo is intended not to collect more information than the functions require.'}
            </p>
          </PolicyCard>

          <PolicyCard title={sv ? 'Tjänster som behandlar uppgifter' : 'Services that process data'}>
            <PolicyList
              items={
                sv
                  ? [
                      ['Supabase', 'backend, autentisering, databas och familjedata.'],
                      ['Firebase Cloud Messaging', 'pushnotiser när användaren har aktiverat notiser.'],
                      ['FormSubmit', 'förmedling av supportformulär; användarens e-postklient kan användas som reservväg.'],
                    ]
                  : [
                      ['Supabase', 'backend, authentication, database and family data.'],
                      ['Firebase Cloud Messaging', 'push notifications when notifications are enabled.'],
                      ['FormSubmit', 'relay of support-form submissions; the user’s email client can be used as a fallback.'],
                    ]
              }
            />
            <p className="mt-4">
              {sv
                ? 'Dessa tjänster används för att driva FamPo och hantera support som användaren själv begär. FamPo säljer inte personuppgifter.'
                : 'These services are used to operate FamPo and handle support requested by the user. FamPo does not sell personal data.'}
            </p>
          </PolicyCard>

          <PolicyCard title={sv ? 'Barnprofiler och barnenheter' : 'Child profiles and child devices'}>
            <p>
              {sv
                ? 'Barnprofiler skapas och hanteras inom den vuxnas familjekonto. Endast förnamn eller smeknamn behövs i barnprofilen. En parad barnenhet använder en begränsad anonym teknisk session och får inte ett normalt konto med egen e-post eller eget lösenord.'
                : 'Child profiles are created and managed within the adult family account. Only a first name or nickname is needed for a child profile. A paired child device uses a restricted anonymous technical session and does not receive a normal account with its own email or password.'}
            </p>
          </PolicyCard>

          <PolicyCard title={sv ? 'Reklam och analys' : 'Advertising and analytics'}>
            <p>
              {sv
                ? 'Den version av FamPo som förbereds för 1.0 innehåller inga annons- eller analys-SDK:er. Om detta ändras i en framtida version måste integritetspolicyn och relevanta butiksdeklarationer uppdateras innan funktionen används i produktion.'
                : 'The version of FamPo being prepared for 1.0 contains no advertising or analytics SDKs. If this changes in a future version, the privacy policy and relevant store disclosures must be updated before the functionality is used in production.'}
            </p>
          </PolicyCard>

          <PolicyCard title={sv ? 'Säkerhet och åtkomst' : 'Security and access'}>
            <p>
              {sv
                ? 'Åtkomst till familjeuppgifter är avsedd att begränsas genom autentisering och backendens behörighetsregler. Kommunikation med FamPos backend- och pushleverantörer sker över deras HTTPS/TLS-baserade tjänstegränssnitt. Skicka inte lösenord, PIN-koder eller andra hemligheter i supportärenden.'
                : 'Access to family data is intended to be restricted through authentication and backend authorization rules. Communication with FamPo’s backend and push providers uses their HTTPS/TLS-based service interfaces. Do not send passwords, PIN codes or other secrets in support requests.'}
            </p>
          </PolicyCard>

          <PolicyCard title={sv ? 'Radering och begränsad retention' : 'Deletion and limited retention'}>
            <p>
              {sv
                ? 'Den vuxna kontoägaren har ett återautentiserat raderingsflöde i klienten. Innan offentlig release måste detta flöde vara verifierat mot den driftsatta backendversionen. En bekräftad lyckad raderingsoperation ska ta bort den vuxna appinloggningen, familjekontot och familjeägt FamPo-innehåll som omfattas av kontraktet, bland annat profiler, uppgifter, poäng/plånböcker, belöningar/inlösen, notiser, önskningar, familjeägda enhets- och parkopplingsuppgifter, medlemskap och inbjudningar.'
                : 'The adult account owner has a reauthenticated deletion flow in the client. Before public release, this flow must be verified against the deployed backend version. A confirmed successful deletion operation is intended to remove the adult app sign-in, family account and family-owned FamPo content covered by the contract, including profiles, tasks, points/wallets, rewards/redemptions, notifications, wishes, family-owned device and pairing data, memberships and invitations.'}
            </p>
            <p className="mt-4">
              {sv
                ? 'Kontraktet kan behålla snäv teknisk servermetadata för idempotens, säker återställning och missbruksskydd: raderingsoperationens status/tidpunkter/serverhärledda identifierare, en profil- och familjefri permanent identitet med frikopplad Auth-länk samt HMAC-baserade rate-limit-identifierare och tidpunkter. Ingen sådan post ska innehålla den raderade familjens profil- eller familjeinnehåll.'
                : 'The contract may retain narrow technical server metadata for idempotency, safe recovery and abuse protection: deletion-operation status/timestamps/server-derived identifiers, a profile- and family-free permanent identity with a detached Auth link, and HMAC-based rate-limit identifiers and timestamps. Such records are not intended to contain the deleted family’s profile or family content.'}
            </p>
            <a
              href={sv ? '/fampo/delete-account/' : '/en/fampo/delete-account/'}
              className="mt-5 inline-flex items-center gap-2 font-bold text-[#7C3AED]"
            >
              {sv ? 'Läs om kontoradering' : 'Read about account deletion'} <ArrowRight size={16} />
            </a>
          </PolicyCard>

          <p className="pt-2 text-center text-sm text-[#8A789F]">
            {sv ? 'Senast granskad: 29 augusti 2026' : 'Last reviewed: 29 August 2026'}
          </p>
        </div>
      </section>
    </main>
  );
}

function DeleteAccountPage({ locale }: { locale: Locale }) {
  const sv = locale === 'sv';
  const subject = sv ? 'Radera FamPo-konto' : 'Delete FamPo account';

  return (
    <main>
      <ResourceHero
        locale={locale}
        eyebrow={sv ? 'Kontoradering' : 'Account deletion'}
        title={sv ? 'Radera ett FamPo-familjekonto.' : 'Delete a FamPo family account.'}
        intro={
          sv
            ? 'Den här sidan är FamPos externa raderingsresurs. Den finns för kontoägare som vill begära radering även om de inte kan använda appen.'
            : 'This page is FamPo’s external deletion resource. It exists for account owners who want to request deletion even if they cannot use the app.'
        }
        icon={Trash2}
      />

      <section className="px-5 pb-24 md:px-10 lg:px-16">
        <div className="mx-auto max-w-[920px] space-y-5">
          <article className="rounded-[30px] border border-[#D9CDEA] bg-white p-7 md:p-9">
            <div className="flex items-start gap-4">
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#F0E8FF] text-[#7C3AED]">
                <Mail size={22} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#8A789F]">
                  {sv ? 'Extern begäran' : 'External request'}
                </p>
                <h2 className="mt-1 font-sora text-2xl font-bold text-[#2D1457]">
                  {sv ? 'Begär radering utan appen' : 'Request deletion without the app'}
                </h2>
              </div>
            </div>

            <p className="mt-6 leading-7 text-[#68577D]">
              {sv
                ? 'Skicka begäran från samma e-postadress som används för den vuxna FamPo-inloggningen. Skriv “Radera FamPo-konto” i ämnesraden. Keevo kan behöva verifiera att du äger kontot innan begäran hanteras.'
                : 'Send the request from the same email address used for the adult FamPo sign-in. Use “Delete FamPo account” as the subject. Keevo may need to verify that you own the account before the request is handled.'}
            </p>

            <a
              href={`mailto:${siteConfig.email}?subject=${encodeURIComponent(subject)}`}
              className="mt-7 inline-flex min-h-12 items-center gap-2 rounded-full bg-[#7C3AED] px-6 font-bold text-white transition hover:bg-[#6D28D9]"
            >
              <Mail size={18} />
              {sv ? 'Begär radering via e-post' : 'Request deletion by email'}
            </a>

            <p className="mt-4 text-sm leading-6 text-[#8A789F]">
              {sv
                ? `Om knappen inte öppnar din e-postapp kan du skicka manuellt till ${siteConfig.email}.`
                : `If the button does not open your email app, send the request manually to ${siteConfig.email}.`}
            </p>
          </article>

          <PolicyCard title={sv ? 'Radering i appen' : 'Deletion in the app'}>
            <ol className="list-decimal space-y-3 pl-5 text-[#68577D]">
              <li>{sv ? 'Öppna Föräldraläge och ange familjens PIN.' : 'Open Parent mode and enter the family PIN.'}</li>
              <li>{sv ? 'Öppna Hantera familjen.' : 'Open Manage family.'}</li>
              <li>{sv ? 'Under Familjekonto, välj Radera familjekonto.' : 'Under Family account, choose Delete family account.'}</li>
              <li>{sv ? 'Bekräfta med kontots lösenord och bekräftelseordet som visas.' : 'Confirm with the account password and the displayed confirmation word.'}</li>
            </ol>

            <div className="mt-6 rounded-2xl border border-[#F2C7D5] bg-[#FFF6F8] p-5 text-sm leading-6 text-[#7C4558]">
              <strong>{sv ? 'Release-status:' : 'Release status:'}</strong>{' '}
              {sv
                ? 'klienten innehåller raderingsflödet, men FamPo är fortfarande i releaseförberedelse och backendkontraktet för kontoradering måste verifieras i produktion före offentlig butikslansering. Om flödet inte är tillgängligt eller visar fel, använd den externa begäransvägen ovan.'
                : 'the client contains the deletion flow, but FamPo is still in release preparation and the account-deletion backend contract must be verified in production before a public store release. If the flow is unavailable or shows an error, use the external request route above.'}
            </div>
          </PolicyCard>

          <PolicyCard title={sv ? 'Vad omfattas av en lyckad radering?' : 'What is covered by a successful deletion?'}>
            <PolicyList
              items={
                sv
                  ? [
                      ['Vuxen inloggning', 'den vuxna FamPo-appinloggningen som äger familjekontot.'],
                      ['Familjekonto', 'familjen och dess medlemskap/inbjudningar.'],
                      ['Profiler och poäng', 'profiler, plånböcker/poäng och tillhörande familjedata.'],
                      ['Uppgifter och historik', 'uppgifter, slutföranden och relaterad familjehistorik som omfattas av kontraktet.'],
                      ['Belöningar och önskningar', 'belöningar, inlösen, önskningar och andra familjeägda poster.'],
                      ['Enhet och parkoppling', 'familjeägda pushregistreringar, parkopplingsuppgifter och relaterade familjeanknutna poster som omfattas av raderingskontraktet.'],
                    ]
                  : [
                      ['Adult sign-in', 'the adult FamPo app sign-in that owns the family account.'],
                      ['Family account', 'the family and its memberships/invitations.'],
                      ['Profiles and points', 'profiles, wallets/points and associated family data.'],
                      ['Tasks and history', 'tasks, completions and related family history covered by the contract.'],
                      ['Rewards and wishes', 'rewards, redemptions, wishes and other family-owned records.'],
                      ['Device and pairing', 'family-owned push registrations, pairing data and related family-scoped records covered by the deletion contract.'],
                    ]
              }
            />
          </PolicyCard>

          <PolicyCard title={sv ? 'Begränsad teknisk metadata som kan behållas' : 'Limited technical metadata that may be retained'}>
            <p>
              {sv
                ? 'För idempotens, säker återställning och missbruksskydd kan raderingskontraktet behålla en snäv mängd servermetadata: raderingsoperationens status, tidpunkter och serverhärledda identifierare; en profil- och familjefri permanent identitet med frikopplad Auth-länk; samt HMAC-baserade rate-limit-identifierare och tidpunkter. Detta ska inte vara ett sätt att behålla den raderade familjens appinnehåll.'
                : 'For idempotency, safe recovery and abuse protection, the deletion contract may retain a narrow set of server metadata: deletion-operation status, timestamps and server-derived identifiers; a profile- and family-free permanent identity with a detached Auth link; and HMAC-based rate-limit identifiers and timestamps. This is not intended as a way to retain the deleted family’s app content.'}
            </p>
          </PolicyCard>

          <PolicyCard title={sv ? 'Barnprofiler' : 'Child profiles'}>
            <p>
              {sv
                ? 'En parad barnenhet är inte ett separat vuxet appkonto. Barnprofilen och den begränsade tekniska barnsessionen hanteras som del av familjekontots modell och ska inte presenteras som ett fristående konto med egen e-post eller eget lösenord.'
                : 'A paired child device is not a separate adult app account. The child profile and restricted technical child session are handled as part of the family-account model and are not presented as a standalone account with its own email or password.'}
            </p>
          </PolicyCard>

          <div className="rounded-[28px] border border-[#DED5EA] bg-[#FCFAFF] p-7 text-center">
            <CheckCircle2 size={24} className="mx-auto text-[#7C3AED]" />
            <h2 className="mt-4 font-sora text-xl font-bold text-[#2D1457]">
              {sv ? 'Frågor om radering eller integritet?' : 'Questions about deletion or privacy?'}
            </h2>
            <a
              href={`mailto:${siteConfig.email}`}
              className="mt-4 inline-flex items-center gap-2 font-bold text-[#7C3AED]"
            >
              {siteConfig.email} <ExternalLink size={15} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

function PolicyCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <article className="rounded-[28px] border border-[#E3DBEF] bg-white p-7 md:p-9">
      <h2 className="font-sora text-2xl font-bold tracking-[-0.025em] text-[#2D1457]">{title}</h2>
      <div className="mt-5 text-[15px] leading-7 text-[#68577D] md:text-base">{children}</div>
    </article>
  );
}

function PolicyList({ items }: { items: Array<[string, string]> }) {
  return (
    <ul className="space-y-4">
      {items.map(([title, body]) => (
        <li key={title} className="flex gap-3">
          <CheckCircle2 size={19} className="mt-1 shrink-0 text-[#7C3AED]" />
          <span>
            <strong className="text-[#372150]">{title}</strong> — {body}
          </span>
        </li>
      ))}
    </ul>
  );
}
